import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  Copy,
  Inbox,
  Info,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { FeeRuleGroup, PaymentFee, ProviderRailRoute } from "../../types/paymentFees.types";
import {
  FEE_STATUS_PILL_VARIANT,
  formatFeeFormula,
  formatRangeLines,
  scopeChipsFor,
  shortId,
} from "../../utils/feeFormat";
import { scopeHistory, type FeeChange, type FeeChangeTrend } from "../../utils/feeHistory";
import { feeScopeOf, scopeKey } from "../../utils/feeMath";

export interface FeeRuleGroupListProps {
  groups: FeeRuleGroup[];
  /** Every loaded row: a version's end is the next version's start, filtered out or not. */
  fees: PaymentFee[];
  now: number;
  exampleAmountFor: (currency: string | null | undefined) => string;
  routes: ProviderRailRoute[];
  authorNames: Record<string, string>;
  organizationNames: Record<string, string>;
  isLoading: boolean;
  expandedKeys: Record<string, boolean>;
  onToggle: (key: string) => void;
  highlightedKey: string | null;
  onNewVersion: (fee: PaymentFee) => void;
  onDuplicate: (fee: PaymentFee) => void;
  onDelete: (fee: PaymentFee) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onCreateFirst: () => void;
  emptyTitle: string;
  /** General scope of the commission tab: organizations' rules are hidden, and the note says why. */
  generalOnlyNote: boolean;
}

const TREND_ICON: Record<FeeChangeTrend, typeof Minus> = {
  NEW: Sparkles,
  UP: ArrowUpRight,
  DOWN: ArrowDownRight,
  SAME: Minus,
};

/** Rules of one fee kind, grouped by exact scope, most specific first — the order the resolver reads them. */
export function FeeRuleGroupList({
  groups,
  fees,
  now,
  exampleAmountFor,
  routes,
  authorNames,
  organizationNames,
  isLoading,
  expandedKeys,
  onToggle,
  highlightedKey,
  onNewVersion,
  onDuplicate,
  onDelete,
  hasActiveFilters,
  onClearFilters,
  onCreateFirst,
  emptyTitle,
  generalOnlyNote,
}: FeeRuleGroupListProps) {
  const { t } = useTranslations("paymentFees");

  const historyByKey = useMemo(() => {
    const byKey = new Map<string, PaymentFee[]>();
    for (const fee of fees) {
      const key = scopeKey(fee.fee_kind, feeScopeOf(fee));
      byKey.set(key, [...(byKey.get(key) ?? []), fee]);
    }
    const result = new Map<string, FeeChange[]>();
    for (const [key, rows] of byKey) {
      result.set(
        key,
        scopeHistory(rows, now, (fee) => exampleAmountFor(fee.currency)),
      );
    }
    return result;
  }, [fees, now, exampleAmountFor]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="rounded-xl border border-border bg-card p-4">
            <ColocaSkeleton className="h-4 w-2/3" />
            <ColocaSkeleton className="mt-3 h-4 w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {generalOnlyNote && <ColocaAlert variant="secondary" icon={Info} text={t("rules_general_only_note")} />}
      {groups.length === 0 ? (
        <ColocaEmpty
          icon={hasActiveFilters ? Inbox : CircleAlert}
          title={hasActiveFilters ? t("empty_filtered_title") : emptyTitle}
          description={hasActiveFilters ? t("empty_filtered_description") : t("empty_description")}
        >
          {hasActiveFilters ? (
            <ColocaButton variant="default" onClick={onClearFilters}>
              {t("empty_filtered_cta")}
            </ColocaButton>
          ) : (
            <ColocaButton variant="primary" icon={Plus} onClick={onCreateFirst}>
              {t("empty_cta")}
            </ColocaButton>
          )}
        </ColocaEmpty>
      ) : (
        groups.map((group) => {
          const visibleIds = new Set(group.rows.map((row) => row.fee.id));
          const changes = (historyByKey.get(group.key) ?? []).filter((change) =>
            visibleIds.has(change.fee.id),
          );
          return (
            <FeeRuleGroupCard
              key={group.key}
              group={group}
              changes={changes}
              routes={routes}
              authorNames={authorNames}
              organizationNames={organizationNames}
              expanded={!!expandedKeys[group.key]}
              highlighted={highlightedKey === group.key}
              onToggle={() => onToggle(group.key)}
              onNewVersion={onNewVersion}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
            />
          );
        })
      )}
    </div>
  );
}

interface FeeRuleGroupCardProps {
  group: FeeRuleGroup;
  changes: FeeChange[];
  routes: ProviderRailRoute[];
  authorNames: Record<string, string>;
  organizationNames: Record<string, string>;
  expanded: boolean;
  highlighted: boolean;
  onToggle: () => void;
  onNewVersion: (fee: PaymentFee) => void;
  onDuplicate: (fee: PaymentFee) => void;
  onDelete: (fee: PaymentFee) => void;
}

function FeeRuleGroupCard({
  group,
  changes,
  routes,
  authorNames,
  organizationNames,
  expanded,
  highlighted,
  onToggle,
  onNewVersion,
  onDuplicate,
  onDelete,
}: FeeRuleGroupCardProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();

  const winner = group.winner;
  const winnerFee = winner.fee;
  const panelId = `fee-group-${group.key.replace(/[^a-z0-9]/gi, "")}`;
  const chips = useMemo(
    () => scopeChipsFor(winnerFee, labels.chips, routes, organizationNames),
    [winnerFee, labels, routes, organizationNames],
  );

  const activeRow = group.rows.find((row) => row.status === "ACTIVE");
  const scheduledRow = group.rows.find((row) => row.status === "SCHEDULED");
  const ranges = winnerFee.tiers?.length ? formatRangeLines(winnerFee, activeLanguage, labels.formula) : [];

  const metaLine = [
    activeRow
      ? t("group_effective_since", {
          date: formatDateTime(activeRow.fee.valid_from, { locale: activeLanguage }),
        })
      : null,
    t("group_versions", { count: group.rows.length }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      className={cn(
        "overflow-hidden rounded-xl border bg-card transition-colors",
        highlighted ? "border-primary" : "border-border",
        !activeRow && winner.status !== "SCHEDULED" && "opacity-75",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={t("group_expand_aria")}
        className="block w-full px-4 py-3.5 text-left hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="flex flex-wrap items-center gap-1.5">
            {chips.map((chip) => (
              <ColocaBadge
                key={chip.id}
                variant={
                  chip.id === "organization" && !chip.muted ? "primary" : chip.muted ? "secondary" : "default"
                }
                className={cn(
                  chip.muted && "border-dashed text-muted-foreground",
                  chip.mono ? "font-mono" : "font-sans",
                )}
              >
                {chip.label}
              </ColocaBadge>
            ))}
          </span>

          <span className="ml-auto flex shrink-0 items-center gap-2">
            {scheduledRow && winner.status !== "SCHEDULED" && (
              <ColocaStatusPill variant="info" size="sm">
                {t("group_scheduled_chip", {
                  date: formatDateTime(scheduledRow.fee.valid_from, { locale: activeLanguage }),
                })}
              </ColocaStatusPill>
            )}
            <ColocaStatusPill variant={FEE_STATUS_PILL_VARIANT[winner.status]} size="sm">
              {labels.status[winner.status]}
            </ColocaStatusPill>
            {expanded ? (
              <ChevronUp className="size-4 text-muted-foreground" aria-hidden="true" />
            ) : (
              <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
            )}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium tabular-nums">
            {formatFeeFormula(winnerFee, activeLanguage, labels.formula)}
          </span>
          {ranges.length > 0 && (
            <ColocaBadge variant="primary" className="font-sans">
              {t("badge_ranges")}
            </ColocaBadge>
          )}
        </div>
        {ranges.length > 0 && <RangeLines fee={winnerFee} />}

        <p className="mt-1 text-xs text-muted-foreground">{metaLine}</p>
      </button>

      {expanded && (
        <div id={panelId} className="space-y-3 border-t border-border bg-muted/30 px-4 py-3">
          <div className="hidden lg:block">
            <VersionTable changes={changes} authorNames={authorNames} onDelete={onDelete} />
          </div>
          <div className="lg:hidden">
            <VersionCards changes={changes} authorNames={authorNames} onDelete={onDelete} />
          </div>

          <div className="flex flex-wrap gap-2">
            <ColocaButton variant="default" size="sm" icon={Pencil} onClick={() => onNewVersion(winnerFee)}>
              {t("group_new_version")}
            </ColocaButton>
            <ColocaButton variant="ghost" size="sm" icon={Copy} onClick={() => onDuplicate(winnerFee)}>
              {t("group_duplicate")}
            </ColocaButton>
          </div>
        </div>
      )}
    </section>
  );
}

interface VersionViewProps {
  changes: FeeChange[];
  authorNames: Record<string, string>;
  onDelete: (fee: PaymentFee) => void;
}

function usePeriod() {
  const { t, activeLanguage } = useTranslations("paymentFees");
  return (change: FeeChange) => {
    const from = formatDateTime(change.fee.valid_from, { locale: activeLanguage });
    if (change.status === "SCHEDULED") return t("period_starts", { from });
    if (!change.effectiveUntil) return t("period_open", { from });
    return t("period_range", { from, to: formatDateTime(change.effectiveUntil, { locale: activeLanguage }) });
  };
}

function TrendBadge({ change }: { change: FeeChange }) {
  const { t } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const Icon = TREND_ICON[change.trend];
  return (
    <span className="inline-flex flex-wrap gap-1">
      <ColocaBadge
        variant={change.trend === "DOWN" ? "warning" : change.trend === "UP" ? "success" : "default"}
        icon={Icon}
        className="font-sans"
      >
        {labels.trend[change.trend]}
      </ColocaBadge>
      {change.isZero && change.role !== "COST" && (
        <ColocaBadge variant="destructive" className="font-sans">
          {t("trend_zero")}
        </ColocaBadge>
      )}
    </span>
  );
}

function VersionMenu({ fee, onDelete }: { fee: PaymentFee; onDelete: (fee: PaymentFee) => void }) {
  const { t } = useTranslations("paymentFees");
  return (
    <ColocaDropdownMenu
      align="end"
      items={[
        { label: t("stack_delete_menu"), icon: Trash2, variant: "destructive", onClick: () => onDelete(fee) },
      ]}
      trigger={
        <ColocaIconButton
          icon={MoreHorizontal}
          variant="ghost"
          size="sm"
          aria-label={t("stack_actions_aria", { version: fee.version })}
        />
      }
    />
  );
}

function VersionTable({ changes, authorNames, onDelete }: VersionViewProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const period = usePeriod();

  const columns = useMemo<ColocaDataTableColumn<FeeChange>[]>(
    () => [
      {
        id: "version",
        header: t("stack_th_version"),
        cell: (change) => <span className="font-mono text-xs">v{change.fee.version}</span>,
      },
      {
        id: "price",
        header: t("stack_th_price"),
        cell: (change) => (
          <span className="flex flex-col gap-1">
            <span className="font-mono text-xs tabular-nums">
              {formatFeeFormula(change.fee, activeLanguage, labels.formula)}
            </span>
            {change.fee.tiers && change.fee.tiers.length > 0 && <RangeLines fee={change.fee} />}
          </span>
        ),
      },
      {
        id: "effective",
        header: t("stack_th_effective"),
        cellClassName: "whitespace-nowrap",
        cell: (change) => <span className="text-xs">{period(change)}</span>,
      },
      {
        id: "change",
        header: t("stack_th_change"),
        cell: (change) => <TrendBadge change={change} />,
      },
      {
        id: "status",
        header: t("stack_th_status"),
        cell: (change) => (
          <ColocaStatusPill variant={FEE_STATUS_PILL_VARIANT[change.status]} size="sm">
            {labels.status[change.status]}
          </ColocaStatusPill>
        ),
      },
      {
        id: "author",
        header: t("stack_th_author"),
        cellClassName: "whitespace-nowrap",
        cell: (change) => <AuthorCell fee={change.fee} authorNames={authorNames} />,
      },
      {
        id: "reason",
        header: t("stack_th_reason"),
        cellClassName: "max-w-56",
        cell: (change) =>
          change.fee.change_reason ? (
            <span className="block max-w-56 truncate text-xs" title={change.fee.change_reason}>
              {change.fee.change_reason}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">{t("stack_empty_value")}</span>
          ),
      },
      {
        id: "created",
        header: t("stack_th_created"),
        cellClassName: "whitespace-nowrap text-muted-foreground",
        cell: (change) => (
          <span className="text-xs">{formatDateTime(change.fee.created_at, { locale: activeLanguage })}</span>
        ),
      },
      {
        id: "actions",
        header: "",
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (change) => <VersionMenu fee={change.fee} onDelete={onDelete} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activeLanguage, labels, authorNames, onDelete],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={changes}
      rowKey={(change) => change.fee.id}
      minWidth={1080}
      frame="none"
      rowClassName={(change) =>
        change.status === "ACTIVE"
          ? "bg-success/5"
          : change.status === "SUPERSEDED" || change.status === "EXPIRED"
            ? "opacity-60"
            : undefined
      }
    />
  );
}

function VersionCards({ changes, authorNames, onDelete }: VersionViewProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const period = usePeriod();

  return (
    <div className="flex flex-col gap-2">
      {changes.map((change) => {
        const dimmed = change.status === "SUPERSEDED" || change.status === "EXPIRED";
        return (
          <article
            key={change.fee.id}
            className={cn(
              "rounded-lg border border-border bg-card p-3",
              change.status === "ACTIVE" && "border-success/40",
              dimmed && "text-muted-foreground",
            )}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs">v{change.fee.version}</span>
              <ColocaStatusPill variant={FEE_STATUS_PILL_VARIANT[change.status]} size="sm">
                {labels.status[change.status]}
              </ColocaStatusPill>
              <span className="ml-auto">
                <VersionMenu fee={change.fee} onDelete={onDelete} />
              </span>
            </div>
            <p className="mt-1.5 font-mono text-sm tabular-nums">
              {formatFeeFormula(change.fee, activeLanguage, labels.formula)}
            </p>
            {change.fee.tiers && change.fee.tiers.length > 0 && <RangeLines fee={change.fee} />}
            <div className="mt-1.5">
              <TrendBadge change={change} />
            </div>
            <p className="mt-1.5 text-xs">{period(change)}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              <AuthorCell fee={change.fee} authorNames={authorNames} /> ·{" "}
              {change.fee.change_reason ?? t("stack_empty_value")}
            </p>
          </article>
        );
      })}
    </div>
  );
}

function AuthorCell({ fee, authorNames }: { fee: PaymentFee; authorNames: Record<string, string> }) {
  const { t } = useTranslations("paymentFees");
  if (!fee.created_by_user_id) {
    return <span className="text-xs text-muted-foreground">{t("stack_empty_value")}</span>;
  }
  const name = authorNames[fee.created_by_user_id];
  return name ? (
    <span className="text-xs">{name}</span>
  ) : (
    <span className="font-mono text-xs text-muted-foreground" title={fee.created_by_user_id}>
      {shortId(fee.created_by_user_id)}
    </span>
  );
}

function RangeLines({ fee }: { fee: PaymentFee }) {
  const { activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  return (
    <ul className="mt-1 flex flex-col gap-0.5 font-mono text-[11px] text-muted-foreground">
      {formatRangeLines(fee, activeLanguage, labels.formula).map((line) => (
        <li key={line.label}>
          {line.label}: {line.price}
        </li>
      ))}
    </ul>
  );
}
